import Drawer from "@mui/material/Drawer";
import ReplacementsFlyout from "./ReplacementsFlyout/ReplacementsFlyout.tsx";
import ShoppingCartFlyout from "./ShoppingCartFlyout";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";

type ActiveFlyout = "replacements" | "shoppingCart" | null;

interface FlyoutHandlerProps {
	showButtons: boolean;
	lineItemInformation?: models["LineItemViewModel"] | null;
	globalInformation?: models["GlobalsViewModel"] | null;
	dataTestId?: string;
	showReplacementTrigger?: boolean;
	showShoppingCartTrigger?: boolean;
}

const FlyoutHandler = ({
	showButtons,
	lineItemInformation,
	globalInformation,
	dataTestId,
	showReplacementTrigger,
	showShoppingCartTrigger
}: FlyoutHandlerProps) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [activeFlyout, setActiveFlyout] = useState<ActiveFlyout>(
		showReplacementTrigger ? "replacements" : "shoppingCart"
	);
	const navigateTo = useNavigate();
	const params = useParams();
	const { isReplacementsReviewPage, isReplacementsReasonPage } = useUserLocation();
	const cartItems = useSelector((state: RootState) => state.cart.replacements);

	const showReplacements = useCallback(() => {
		setActiveFlyout("replacements");
		setIsOpen(true);
	}, []);

	const showShoppingCart = useCallback(() => {
		setActiveFlyout("shoppingCart");
		setIsOpen(true);
	}, []);

	const hideFlyouts = useCallback(() => {
		if ((isReplacementsReasonPage || isReplacementsReviewPage) && cartItems.length <= 0) {
			navigateTo(`/replacements/${params?.orderId}`);
		}
		setIsOpen(false);
		setActiveFlyout(null);
	}, [cartItems.length, params?.orderId, isReplacementsReasonPage, isReplacementsReviewPage, navigateTo]);

	useEffect(() => {
		if (showReplacementTrigger) {
			showReplacements();
		}
	}, [isOpen, showReplacementTrigger, showReplacements]);

	useEffect(() => {
		if (showShoppingCartTrigger) {
			showShoppingCart();
		}
	}, [showShoppingCartTrigger, showShoppingCart]);

	return (
		<Drawer
			anchor="right"
			open={isOpen}
			onClose={hideFlyouts}
			{...(dataTestId ? { "data-testid": dataTestId } : {})}
		>
			{activeFlyout === "replacements" && (
				<ReplacementsFlyout
					lineItemInformation={lineItemInformation}
					globalInformation={globalInformation}
					addToOrder={showShoppingCart}
					closeDrawer={hideFlyouts}
				/>
			)}

			{activeFlyout === "shoppingCart" && (
				<ShoppingCartFlyout
					showButtons={showButtons}
					edit={showReplacements}
					closeDrawer={hideFlyouts}
				/>
			)}
		</Drawer>
	);
};

export default FlyoutHandler;
